import React, { useState, useEffect, useRef } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { X, Upload, Loader2, Save, Image as ImageIcon } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { compressImage, GALLERY_PRESET, validateImageFile, formatFileSize } from '../../utils/imageCompression';

/**
 * TraderFeatures interface matching the trader_features table structure.
 * Used for all business types (traders, crafties, hospitality, professionals).
 */
export interface TraderFeatures {
  user_id: string;
  feature1_enabled?: boolean;
  feature1_title?: string;
  feature1_description?: string;
  feature1_image?: string;
  feature2_enabled?: boolean;
  feature2_title?: string;
  feature2_description?: string;
  feature2_image?: string;
  feature3_enabled?: boolean;
  feature3_title?: string;
  feature3_description?: string;
  feature3_image?: string;
  created_at?: string;
  updated_at?: string;
}

interface AdminFeatureManagerProps {
  /** The user_id associated with the business (from traders, crafties, hospitality, or professionals table) */
  userId: string;
  /** Business name for display purposes */
  businessName: string;
  /** Callback when features are saved successfully */
  onSave?: (features: TraderFeatures) => void;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * AdminFeatureManager - A reusable component for managing trader_features from the admin panel.
 * This component allows admins to create/edit feature cards (title, description, image, enabled status)
 * for any business type that uses the trader_features table.
 */
const AdminFeatureManager: React.FC<AdminFeatureManagerProps> = ({
  userId,
  businessName,
  onSave,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState<TraderFeatures | null>(null);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);

  // Refs for file inputs
  const fileInput1Ref = useRef<HTMLInputElement>(null);
  const fileInput2Ref = useRef<HTMLInputElement>(null);
  const fileInput3Ref = useRef<HTMLInputElement>(null);

  // Fetch existing features on mount
  useEffect(() => {
    const fetchFeatures = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('trader_features')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error && error.code !== 'PGRST116') {
          // PGRST116 = no rows returned, which is fine for new records
          console.error('Error fetching features:', error);
        }

        if (data) {
          setFeatures(data);
        } else {
          // Initialize with default values
          setFeatures({
            user_id: userId,
            feature1_enabled: false,
            feature1_title: '',
            feature1_description: '',
            feature1_image: '',
            feature2_enabled: false,
            feature2_title: '',
            feature2_description: '',
            feature2_image: '',
            feature3_enabled: false,
            feature3_title: '',
            feature3_description: '',
            feature3_image: '',
          });
        }
      } catch (err) {
        console.error('Error fetching features:', err);
        toast({
          title: 'Error',
          description: 'Failed to load feature data',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, [userId]);

  // Handle image upload for a specific feature
  const handleImageUpload = async (featureNum: 1 | 2 | 3, file: File) => {
    if (!file || !userId) return;

    const imageKey = `feature${featureNum}_image` as keyof TraderFeatures;
    setUploadingImage(imageKey);

    try {
      // Validate source file before processing
      const validation = validateImageFile(file, 20);
      if (!validation.isValid) {
        toast({
          title: 'Invalid image',
          description: validation.error ?? 'Please select a smaller image file.',
          variant: 'destructive',
        });
        return;
      }

      // Compress image to reduce size before upload while keeping good quality
      const originalSize = file.size;
      const compressedFile = await compressImage(file, GALLERY_PRESET);
      const compressedSize = compressedFile.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(0);

      // Generate unique filename based on original extension
      const originalExt = file.name.split('.').pop() || 'jpg';
      const fileExt = originalExt.toLowerCase();
      const fileName = `${userId}_feature${featureNum}_${uuidv4()}.${fileExt}`;
      const filePath = `features/${userId}/${fileName}`;

      // Upload compressed image to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressedFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setFeatures((prev) =>
          prev
            ? {
                ...prev,
                [imageKey]: urlData.publicUrl,
              }
            : null
        );
        toast({
          title: 'Success',
          description: `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`,
        });
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(null);
    }
  };

  // Handle saving features to the database
  const handleSave = async () => {
    if (!features || !userId) return;

    setSaving(true);

    try {
      const updateData = {
        ...features,
        user_id: userId,
        updated_at: new Date().toISOString(),
      };

      const { data: existingRows, error: existingError } = await supabaseAdmin
        .from('trader_features')
        .select('user_id')
        .eq('user_id', userId);

      if (existingError) {
        console.error('Error checking existing features record:', existingError);
      }

      const hasExisting = Array.isArray(existingRows) && existingRows.length > 0;

      let result;
      if (hasExisting) {
        const { data, error } = await supabaseAdmin
          .from('trader_features')
          .update(updateData)
          .eq('user_id', userId)
          .select();

        if (error) throw error;
        result = data && data.length > 0 ? data[0] : null;
      } else {
        const { data, error } = await supabaseAdmin
          .from('trader_features')
          .insert({
            ...updateData,
            created_at: new Date().toISOString(),
          })
          .select();

        if (error) throw error;
        result = data && data.length > 0 ? data[0] : null;
      }

      toast({
        title: 'Success',
        description: 'Features saved successfully',
      });

      if (onSave && result) {
        onSave(result);
      }

      onClose();
    } catch (err) {
      console.error('Error saving features:', err);
      toast({
        title: 'Error',
        description: 'Failed to save features',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Update a specific feature field
  const updateFeatureField = (
    featureNum: 1 | 2 | 3,
    field: 'enabled' | 'title' | 'description' | 'image',
    value: string | boolean
  ) => {
    const key = `feature${featureNum}_${field}` as keyof TraderFeatures;
    setFeatures((prev) =>
      prev
        ? {
            ...prev,
            [key]: value,
          }
        : null
    );
  };

  // Render a single feature card editor
  const renderFeatureEditor = (featureNum: 1 | 2 | 3) => {
    if (!features) return null;

    const enabledKey = `feature${featureNum}_enabled` as keyof TraderFeatures;
    const titleKey = `feature${featureNum}_title` as keyof TraderFeatures;
    const descKey = `feature${featureNum}_description` as keyof TraderFeatures;
    const imageKey = `feature${featureNum}_image` as keyof TraderFeatures;

    const isEnabled = features[enabledKey] as boolean;
    const title = (features[titleKey] as string) || '';
    const description = (features[descKey] as string) || '';
    const imageUrl = (features[imageKey] as string) || '';

    const fileInputRef =
      featureNum === 1
        ? fileInput1Ref
        : featureNum === 2
        ? fileInput2Ref
        : fileInput3Ref;

    return (
      <div
        key={featureNum}
        className={`border rounded-lg p-4 ${
          isEnabled ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Feature {featureNum}</h4>
          <label className="flex items-center cursor-pointer">
            <span className="mr-2 text-sm text-gray-600">
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) =>
                  updateFeatureField(featureNum, 'enabled', e.target.checked)
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 rounded-full transition-colors ${
                  isEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
                    isEnabled ? 'translate-x-5' : 'translate-x-1'
                  } mt-0.5`}
                />
              </div>
            </div>
          </label>
        </div>

        <div className="space-y-3">
          {/* Image preview and upload */}
          <div className="flex items-start gap-3">
            <div className="w-20 h-20 rounded-lg border border-gray-200 bg-white overflow-hidden flex-shrink-0">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={`Feature ${featureNum}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      '/bally-Market-sign-200px.png';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImageUpload(featureNum, file);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage === imageKey}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {uploadingImage === imageKey ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload Image
              </button>
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => updateFeatureField(featureNum, 'image', '')}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Remove image
                </button>
              )}
            </div>
          </div>

          {/* Title input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) =>
                updateFeatureField(featureNum, 'title', e.target.value)
              }
              placeholder="Feature title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Description input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) =>
                updateFeatureField(featureNum, 'description', e.target.value)
              }
              placeholder="Feature description"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading features...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Manage Features
            </h2>
            <p className="text-sm text-gray-500">{businessName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <p className="text-sm text-gray-600 mb-4">
            Configure up to 3 feature cards that will be displayed on the public
            business profile. Each feature can have a title, description, and
            image.
          </p>

          <div className="space-y-4">
            {renderFeatureEditor(1)}
            {renderFeatureEditor(2)}
            {renderFeatureEditor(3)}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Features
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFeatureManager;
